/**
 * @fileoverview Writing a theme, and the refusal that is specific to themes.
 *
 * The read path has shipped since 2026-09-15 and nothing of ours wrote a theme
 * until now, so every theme a reader could meet was published by hand. This is
 * the other half.
 *
 * IT INHERITS THE PAGE PUBLISHER'S DISCIPLINES rather than re-deriving them,
 * because a theme is an addressable event republished over its own previous
 * version and has every hole a page has:
 *
 *   - the size is measured against the TARGET relay's advertised limit and
 *     refused locally, before the signer is asked, because an oversized message
 *     closes the socket with no `OK` and no `NOTICE`;
 *   - the timestamp is kept strictly increasing PER ADDRESS, because two writes
 *     inside one second are both accepted with a positive acknowledgement and
 *     the relay then keeps the one with the LOWER event id. The author is told
 *     it worked and the newer version is gone.
 *
 * Neither is likely to bite a theme -- two dozen short token values will never
 * approach half a megabyte -- and both are here anyway. The one that actually
 * fires is the second: a theme is the thing an author fiddles with, nudging a
 * colour and saving again, which is precisely the one-second window.
 *
 * ---
 *
 * THE REFUSAL THAT IS SPECIFIC TO THEMES: A VALUE THE READER WILL DROP.
 *
 * `parseThemeEvent` accepts a theme whose tokens are ALL rejected and returns it
 * as a theme that changes nothing. That is right on the READING side -- a
 * reader has no use for the complaint and no ability to act on it, and the page
 * renders in defaults, which is the graceful-degradation contract.
 *
 * Published from an editor it is the opposite. An author who types `dark-blue`
 * into a colour field and presses save would be told it worked, would open
 * their page, and would find it unchanged with nothing anywhere saying why.
 * That is the same false success as the same-second overwrite: not a failure
 * the author can wait out, but a success report with nothing behind it. So a
 * theme carrying a value the reader would drop is REFUSED HERE, before the
 * signer, with the offending token names returned.
 *
 * THE CHECK IS THE READER'S OWN PARSER, RUN OVER WHAT WE JUST BUILT. There is
 * no second grammar in this file and there must never be one. Two copies of a
 * CSS value grammar would eventually differ by one character, and the symptom
 * would be a publisher that accepts what the reader silently drops -- which is
 * the exact defect this refusal exists to prevent, reintroduced by the fix for
 * it. Building tags and parsing them back also means the writer and the reader
 * are proved to agree by construction rather than by a test remembering to.
 *
 * ---
 *
 * A THEME WITH NO TOKENS AT ALL IS ALLOWED, and that is a deliberate exception
 * to the paragraph above. It looks like the same defect: a theme that changes
 * nothing, published as a success. It is not, because it is a REAL ACT with a
 * real effect. One theme can be referenced by many pages, so emptying it is how
 * an author returns fifty pages to default styling in one edit. Refusing it
 * would refuse the undo of the feature's headline capability. An editor should
 * still say what an empty theme does before saving one; a publisher that made
 * it impossible would be wrong.
 *
 * ---
 *
 * NOTHING HERE IS ENCRYPTED AND THERE IS NO PRIVATE THEME. A theme is fetched
 * by everyone who reads any page referencing it, so a theme only its author
 * could decrypt would be a theme nobody could render. The absence is structural
 * rather than a feature not built yet, which is why no visibility appears
 * anywhere in this file.
 */

import type { SignerInterface } from '@cloistr/auth/core';
import type { Event, EventTemplate, UnsignedEvent } from 'nostr-tools';

import { PAGE_THEME_KIND } from './kinds';
import { checkName } from './nameCheck';
import { measureEventFrame, nextCreatedAt } from './publishPage';
import { isValidIdentifier } from './slug';
import { type ThemeDraft, buildThemeTags, parseThemeEvent } from './theme';

import { encodeAddress } from '@/services/nostr/pageLinks';
import { fetchRelayLimits } from '@/services/nostr/relayInfo';
import { publishEvent, relaysFor } from '@/services/nostr/relays';

export interface PublishedThemeAddress {
  kind: number;
  pubkey: string;
  identifier: string;
}

export type ThemePublishOutcome =
  | {
      status: 'published';
      address: PublishedThemeAddress;
      naddr: string;
      coordinate: string;
      acceptedBy: string[];
    }
  /** A name that could not be part of an address. */
  | { status: 'invalid-name'; identifier: string }
  /**
   * One or more values the reader would drop, named so an editor can point at
   * the fields. See the file comment: publishing these is a false success.
   */
  | { status: 'invalid-tokens'; rejected: string[] }
  /** Refused locally, before sending, so the author keeps their work. */
  | { status: 'too-large'; bytes: number; limit: number; advertised: boolean; relay: string }
  /**
   * A NEW theme at a name this author already holds. Publishing it would
   * replace that theme, restyling every page that wears it. Refused unless the
   * caller says it is replacing it.
   */
  | { status: 'name-taken'; identifier: string }
  /** Not known to be free, because these relays never answered. Refused. */
  | { status: 'name-unchecked'; identifier: string; relays: string[] }
  /** Every relay refused it, in their own words. */
  | { status: 'rejected'; reasons: Record<string, string> }
  | { status: 'signer-failed'; message: string };

/**
 * A pubkey for the validation parse, which happens before we have a real one.
 *
 * `parseThemeEvent` insists on a well-formed pubkey because it is a reader
 * checking somebody else's event. Here the event is ours and unsigned, and the
 * only question being asked is whether the values survive their grammars --
 * which the author's key has no bearing on. Borrowing a placeholder is what
 * lets the token refusal happen with NO signer contact at all, and that is the
 * point: an author whose colour is misspelled should not be asked to approve
 * anything, let alone woken by a notification on their phone.
 */
const UNSIGNED_PUBKEY = '0'.repeat(64);

/**
 * Write a theme to the relays.
 *
 * ORDER OF REFUSALS IS DELIBERATE, cheapest first, and the first two need no
 * network and no signer: a name that cannot be an address, then a value the
 * reader would drop, then the size against the relay that will receive it.
 */
export async function publishTheme(
  draft: ThemeDraft,
  signer: SignerInterface,
  relays: readonly string[] = relaysFor([]),
  /** True when this publish is an edit of the theme already at this name. */
  { replacing = false }: { replacing?: boolean } = {}
): Promise<ThemePublishOutcome> {
  const identifier = draft.identifier.trim().toLowerCase();
  if (!isValidIdentifier(identifier)) {
    return { status: 'invalid-name', identifier: draft.identifier };
  }

  const { tags, content } = buildThemeTags({ ...draft, identifier });

  // The reader's own parser, over the tags we just built. See the file comment
  // on why this is not a second grammar and must not become one.
  const asRead = parseThemeEvent(
    { kind: PAGE_THEME_KIND, pubkey: UNSIGNED_PUBKEY, created_at: 0, content, tags },
    PAGE_THEME_KIND
  );
  if (asRead === null) {
    // Unreachable through `buildThemeTags`, which always writes a `d` tag and
    // is handed the kind just above. Returned rather than thrown, because a
    // publisher that throws on an impossible branch is a publisher that loses
    // an author's work to a refactor nobody tested.
    return { status: 'invalid-name', identifier: draft.identifier };
  }
  if (asRead.rejected.length > 0) {
    return { status: 'invalid-tokens', rejected: asRead.rejected };
  }

  let pubkey: string;
  try {
    pubkey = await signer.getPublicKey();
  } catch (e) {
    return { status: 'signer-failed', message: signerMessage(e) };
  }

  const coordinate = `${PAGE_THEME_KIND}:${pubkey}:${identifier}`;

  // A theme is worn by many pages, so landing a new one on an existing name
  // silently restyles all of them. Only an edit may write over it.
  if (!replacing) {
    const name = await checkName([PAGE_THEME_KIND], pubkey, identifier, [...relays]);
    if (name.status === 'taken') return { status: 'name-taken', identifier };
    if (name.status === 'unchecked') {
      return { status: 'name-unchecked', identifier, relays: name.relays };
    }
  }

  const unsigned: UnsignedEvent = {
    kind: PAGE_THEME_KIND,
    pubkey,
    created_at: nextCreatedAt(coordinate),
    content,
    tags,
  };

  const target = relays[0];
  if (!target) return { status: 'rejected', reasons: {} };

  const bytes = measureEventFrame(unsigned);
  const limits = await fetchRelayLimits(target);
  if (bytes > limits.maxMessageBytes) {
    return {
      status: 'too-large',
      bytes,
      limit: limits.maxMessageBytes,
      advertised: limits.advertised,
      relay: target,
    };
  }

  let signed: Event;
  try {
    signed = await signer.signEvent(unsigned);
  } catch (e) {
    return { status: 'signer-failed', message: signerMessage(e) };
  }

  const { accepted, refused } = await publishEvent([...relays], signed, (template: EventTemplate) =>
    signer.signEvent({ ...template, pubkey })
  );

  if (accepted.length === 0) {
    const reasons: Record<string, string> = {};
    for (const r of refused) reasons[r.relay] = r.reason;
    return { status: 'rejected', reasons };
  }

  const address = { kind: PAGE_THEME_KIND, pubkey, identifier };
  return {
    status: 'published',
    address,
    naddr: encodeAddress({ ...address, relays: accepted }),
    // The form a page's `theme` tag carries. Returned with the outcome so the
    // caller never assembles it by hand from three fields it happens to have.
    coordinate,
    acceptedBy: accepted,
  };
}

/**
 * Repeat the signer's own words rather than flattening them.
 *
 * A remote signer's refusal is often the only thing that tells an author their
 * phone is asleep or their approval expired, and "publishing failed" tells them
 * nothing they can act on.
 */
function signerMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
